<script setup lang="ts">
/**
 * IconBox — icon (or image) inside a rounded tinted box.
 *
 * Sizes:
 *   sm  → 30×30px, radius 8px,  icon 14px
 *   md  → 32×32px, radius 8px,  icon 14px
 *   lg  → 36×36px, radius 10px, icon 16px
 *   xl  → 46×46px, radius 12px, icon 20px
 */
import Box from '@uiKit/elements/Box.vue'
import AppImage from '@uiKit/elements/Image.vue'
import PiIcon, { type PiIconName } from '@uiKit/elements/PiIcon.vue'

export type IconBoxSize = 'sm' | 'md' | 'lg' | 'xl'

interface Props {
  /** PiIcon name (no `pi-` prefix). Ignored when `imageSrc` is set. */
  icon?: PiIconName
  imageSrc?: string
  imageAlt?: string
  size?: IconBoxSize
  /** Background color (CSS color). Default tinted lavender. */
  bg?: string
  /** Icon color (CSS color). Default `--p-primary-600`. */
  iconColor?: string
}

withDefaults(defineProps<Props>(), {
  size: 'lg',
})
</script>

<template>
  <Box
    flex
    align="center"
    justify="center"
    :class="['icon-box', `icon-box--${size}`]"
    :style="bg ? { background: bg } : undefined"
  >
    <AppImage v-if="imageSrc" :src="imageSrc" :alt="imageAlt ?? ''" class="icon-box__img" />
    <PiIcon
      v-else-if="icon"
      :name="icon"
      class="icon-box__icon"
      :style="iconColor ? { color: iconColor } : undefined"
    />
  </Box>
</template>

<style scoped>
.icon-box {
  background: var(--p-primary-50);
  flex-shrink: 0;
}

.icon-box__img {
  display: block;
  max-width: 20px;
  max-height: 20px;
  width: auto;
  height: auto;
}

/* ── Sizes ── */
.icon-box--sm {
  width: 30px;
  height: 30px;
  border-radius: 8px;
}

.icon-box--md {
  width: 32px;
  height: 32px;
  border-radius: 8px;
}

.icon-box--lg {
  width: 36px;
  height: 36px;
  border-radius: 10px;
}

.icon-box--xl {
  width: 46px;
  height: 46px;
  border-radius: 12px;
}

/* ── Icon sizing per box size ── */
.icon-box--sm .icon-box__icon,
.icon-box--md .icon-box__icon {
  font-size: 14px;
  color: var(--p-primary-600);
}

.icon-box--lg .icon-box__icon {
  font-size: 16px;
  color: var(--p-primary-600);
}

.icon-box--xl .icon-box__icon {
  font-size: 20px;
  color: var(--p-primary-600);
}
</style>
